import React, { useState, useCallback, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, ChevronLeft, ChevronRight } from "lucide-react";
import { Button } from "@core/index";
import { useToast } from "@packages/contexts/ToastContext";
import CreateModal, { CreateModalProps } from "../index";
import StepIndicator, { Step } from "./StepIndicator";
import { ThemeColor } from "../OutputLanguageSelector";
import { buttonClasses, gradientClasses } from "../constants";
import LoadingContainer from "@packages/components/Loading";
import ErrorComponent from "../../error-component";

export interface StepComponentProps<T = any> {
  data: T;
  onDataChange: (data: Partial<T>) => void;
  onNext: () => void;
  onPrevious: () => void;
  isValid: boolean;
  themeColor: ThemeColor;
}

export interface MultiStepConfig<T = any> {
  id: string;
  title: string;
  description?: string;
  icon?: React.ReactNode;
  component: React.ComponentType<StepComponentProps<T>>;
  validation?: (data: T) => boolean;
}

export interface MultiStepGradeModalProps<T = any> {
  // Basic props from CreateModal
  title: string;
  themeColor: ThemeColor;
  backPath: string;
  isLoading: boolean;
  error: Error | null;
  loadingTitle: string;
  errorTitle: string;
  errorDescription: string;

  // CreateModal specific props
  createModalProps: Omit<CreateModalProps, "onSubmit">;

  // Multi-step specific props
  steps: MultiStepConfig<T>[];
  initialData: T;
  onFinalSubmit: (data: T) => Promise<void>;

  // Optional props
  enableBasicMode?: boolean;
  basicModeSubmitText?: string;
  advancedModeSubmitText?: string;
}

const MultiStepGradeModal = <T extends Record<string, any>>({
  themeColor,
  backPath,
  isLoading,
  error,
  loadingTitle,
  errorTitle,
  errorDescription,
  createModalProps,
  steps,
  initialData,
  onFinalSubmit,
  advancedModeSubmitText,
}: MultiStepGradeModalProps<T>) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { addToast } = useToast();

  // State management
  const [currentStep, setCurrentStep] = useState(0);
  const [stepData, setStepData] = useState<T>(initialData);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<Error | null>(null);

  // Smooth scroll to top on step change
  useEffect(() => {
    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  }, [currentStep]);

  // Create steps array including the initial CreateModal step
  const allSteps: Step[] = [
    {
      id: "basic-info",
      title: t("common.basicInformation", "Basic Information"),
    },
    ...steps.map((step) => ({
      id: step.id,
      title: step.title,
      icon: step.icon,
    })),
  ];

  // Data manipulation handlers
  const handleDataChange = useCallback((newData: Partial<T>) => {
    setStepData((prev) => ({ ...prev, ...newData }));
  }, []);

  const handleCreateModalSubmit = useCallback(
    async (createModalData: any) => {
      // Update the step data with CreateModal results
      handleDataChange(createModalData as Partial<T>);

      if (steps.length > 0) {
        setCurrentStep(1);
      } else {
        // If no additional steps, submit directly
        if (!isLoading) {
          setIsSubmitting(true);
        }
        setSubmitError(null);

        try {
          await onFinalSubmit({ ...stepData, ...createModalData } as T);
        } catch (error) {
          setSubmitError(
            error instanceof Error ? error : new Error(String(error)),
          );
        } finally {
          if (!isLoading) {
            setIsSubmitting(false);
          }
        }
      }
    },
    [stepData, onFinalSubmit, handleDataChange, steps.length],
  );

  // Step navigation
  const handleNextStep = useCallback(async () => {
    const stepIndex = currentStep - 1; // Adjust for CreateModal being step 0

    if (stepIndex >= 0 && stepIndex < steps.length) {
      const step = steps[stepIndex];
      const isValidStep = step.validation ? step.validation(stepData) : true;

      if (!isValidStep) {
        addToast({
          title: t("notifications.stepValidation.invalid"),
          variant: "error",
          duration: 3000,
        });
        return;
      }
    }

    if (currentStep < allSteps.length - 1) {
      setCurrentStep((prev) => prev + 1);
    } else {
      // Final step - submit all data
      if (!isLoading) {
        setIsSubmitting(true);
      }
      setSubmitError(null);

      try {
        await onFinalSubmit(stepData);
      } catch (error) {
        setSubmitError(
          error instanceof Error ? error : new Error(String(error)),
        );
      } finally {
        if (!isLoading) {
          setIsSubmitting(false);
        }
      }
    }
  }, [
    currentStep,
    steps,
    stepData,
    allSteps.length,
    onFinalSubmit,
    addToast,
    t,
  ]);

  const handlePreviousStep = useCallback(() => {
    if (currentStep > 0) {
      setCurrentStep((prev) => prev - 1);
    }
  }, [currentStep]);

  // Get current step validation status
  const isCurrentStepValid = useCallback(() => {
    if (currentStep === 0) {
      // CreateModal step - let CreateModal handle its own validation
      return true;
    }

    const stepIndex = currentStep - 1;
    if (stepIndex >= 0 && stepIndex < steps.length) {
      const step = steps[stepIndex];
      return step.validation ? step.validation(stepData) : true;
    }

    return true;
  }, [currentStep, steps, stepData]);

  // Render current step content
  const renderStepContent = () => {
    if (currentStep === 0) {
      // Render CreateModal as first step
      return (
        <CreateModal
          {...createModalProps}
          themeColor={themeColor}
          onSubmit={handleCreateModalSubmit}
          isLoading={isLoading}
          error={error}
          hideBackButton={true}
          after={null} // Remove any after content since navigation is handled by MultiStepGradeModal
        />
      );
    }

    // Render custom step components
    const stepIndex = currentStep - 1;
    if (stepIndex >= 0 && stepIndex < steps.length) {
      const step = steps[stepIndex];
      const StepComponent = step.component;

      return (
        <StepComponent
          data={stepData}
          onDataChange={handleDataChange}
          onNext={handleNextStep}
          onPrevious={handlePreviousStep}
          isValid={isCurrentStepValid()}
          themeColor={themeColor}
        />
      );
    }

    return null;
  };

  // Calculate progress
  const progress = ((currentStep + 1) / allSteps.length) * 100;
  const isFirstStep = currentStep === 0;
  const isLastStep = currentStep === allSteps.length - 1;

  // Show loading state during submission or external loading (polling)
  if (isSubmitting || isLoading) {
    return <LoadingContainer title={loadingTitle} themeColor={themeColor} />;
  }

  // Show error state if submission failed or external error
  if (submitError || error) {
    return (
      <ErrorComponent
        title={errorTitle}
        description={
          errorDescription ||
          submitError?.message ||
          error?.message ||
          "An error occurred"
        }
      />
    );
  }

  return (
    <div
      className={`min-h-screen bg-gradient-to-br from-gray-50 to-${themeColor}-50 dark:from-gray-900 dark:to-${themeColor}-900`}
    >
      <div className="container max-w-7xl mx-auto px-4 py-4 relative">
        {/* Header with back button and progress */}
        <div className="mb-6">
          <div className="flex items-center justify-between mb-4">
            <Button
              onClick={() => navigate(backPath)}
              variant="solid"
              size="md"
              textColor="text-white dark:text-white"
              bgColor={buttonClasses[themeColor]}
              rounded="lg"
              disabled={isSubmitting || isLoading}
            >
              <ArrowLeft size={24} />
            </Button>
          </div>
        </div>

        {/* Main content */}
        <div className="max-w-4xl mx-auto">
          {currentStep === 0 ? (
            <div className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-lg rounded-2xl shadow-xl border border-gray-200/50 dark:border-gray-700/50 overflow-hidden">
              {/* Step indicator as header inside the modal */}
              <div className="px-8 py-6 bg-gray-50/50 dark:bg-gray-900/50 border-b border-gray-200/50 dark:border-gray-700/50">
                <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2 mb-4">
                  <div
                    className={`bg-gradient-to-r ${gradientClasses[themeColor]} h-2 rounded-full transition-all duration-300`}
                    style={{ width: `${progress}%` }}
                  />
                </div>
                <StepIndicator
                  steps={allSteps}
                  currentStep={currentStep}
                  themeColor={themeColor}
                  className="w-full"
                />
              </div>

              {/* CreateModal content */}
              <div className="w-full max-w-full overflow-hidden">
                {renderStepContent()}
              </div>
            </div>
          ) : (
            <div className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-lg rounded-2xl shadow-xl border border-gray-200/50 dark:border-gray-700/50 overflow-hidden">
              {/* Step indicator as header inside the modal */}
              <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2 mb-4">
                <div
                  className={`bg-gradient-to-r ${gradientClasses[themeColor]} h-2 rounded-full transition-all duration-300`}
                  style={{ width: `${progress}%` }}
                />
              </div>
              <div className="px-8 py-6 bg-gray-50/50 dark:bg-gray-900/50 border-b border-gray-200/50 dark:border-gray-700/50">
                <StepIndicator
                  steps={allSteps}
                  currentStep={currentStep}
                  themeColor={themeColor}
                  className="w-full"
                />
              </div>

              {/* Step content */}
              <div className="p-8">
                <div className="w-full max-w-full overflow-hidden">
                  {renderStepContent()}
                </div>
              </div>

              {/* Navigation buttons for custom steps */}
              {currentStep > 0 && (
                <div className="flex justify-between items-center px-8 py-6 bg-gray-50/30 dark:bg-gray-900/30 border-t border-gray-200/50 dark:border-gray-700/50">
                  <Button
                    onClick={handlePreviousStep}
                    disabled={isFirstStep || isSubmitting || isLoading}
                    variant="solid"
                    size="md"
                    rounded="lg"
                    bgColor="bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600"
                    textColor="text-gray-700 dark:text-gray-300"
                    className="flex items-center gap-2"
                  >
                    <ChevronLeft size={16} />
                    {t("common.previous", "Previous")}
                  </Button>

                  <Button
                    onClick={handleNextStep}
                    disabled={
                      !isCurrentStepValid() || isSubmitting || isLoading
                    }
                    variant="solid"
                    size="md"
                    rounded="lg"
                    bgColor={buttonClasses[themeColor]}
                    textColor="text-white"
                    className="flex items-center gap-2"
                  >
                    {isSubmitting || isLoading ? (
                      <div className="flex items-center gap-2">
                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                        {t("common.creating", "Creating...")}
                      </div>
                    ) : (
                      <>
                        {isLastStep
                          ? advancedModeSubmitText ||
                            t("common.create", "Create")
                          : t("common.next", "Next")}
                        {!isLastStep && <ChevronRight size={16} />}
                      </>
                    )}
                  </Button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default MultiStepGradeModal;
